/**
 * DashboardGrid Component - Drag-and-drop KPI card grid layout
 *
 * v2.96 - 2026-02-07 - Initial implementation using react-grid-layout
 * - Wraps KPICardGrid data with drag-and-drop positioning
 * - Edit mode: cards are draggable/resizable
 * - View mode: cards are static
 * - Layout persisted to PUT /api/dashboard/config
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Responsive, WidthProvider, Layout } from 'react-grid-layout';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Alert,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Chip,
  IconButton,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Close as CloseIcon,
  DragIndicator as DragIcon,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { Sparklines, SparklinesLine } from 'react-sparklines';
import {
  dashboardKPIService,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  DashboardKPIsResponse,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  DashboardCardsResponse,
  KPIValue,
  KPICardDefinition,
} from '../services/api';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

// v2.96 - Format value (simplified from KPICardGrid)
const formatValue = (
  value: number | null,
  format: string,
  formatOptions?: { currency?: string; decimals?: number; abbreviate?: boolean }
): string => {
  if (value === null) return '--';
  const decimals = formatOptions?.decimals ?? 0;
  const abbreviate = formatOptions?.abbreviate ?? false;

  if (abbreviate && Math.abs(value) >= 1000000) {
    const v = value / 1000000;
    return format === 'currency' ? `$${v.toFixed(1)}M` : `${v.toFixed(1)}M`;
  }
  if (abbreviate && Math.abs(value) >= 1000) {
    const v = value / 1000;
    return format === 'currency' ? `$${v.toFixed(1)}K` : `${v.toFixed(1)}K`;
  }
  if (format === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: formatOptions?.currency || 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  }
  if (format === 'percentage') return `${value.toFixed(decimals)}%`;
  return value.toLocaleString();
};

interface DashboardGridProps {
  editMode: boolean;
  onCardClick?: (cardId: string) => void;
  onRemoveCard?: (cardId: string) => void;
  onLayoutChange?: (layout: Array<{ cardId: string; x: number; y: number; w: number; h: number }>) => void;
}

const DashboardGrid: React.FC<DashboardGridProps> = ({
  editMode,
  onCardClick,
  onRemoveCard,
  onLayoutChange,
}) => {
  const theme = useTheme();
  const [cards, setCards] = useState<KPICardDefinition[]>([]);
  const [kpis, setKpis] = useState<Record<string, KPIValue>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [layouts, setLayouts] = useState<{ [breakpoint: string]: Layout[] }>({});

  // v2.96 - Fetch cards and KPIs
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [cardsRes, kpisRes] = await Promise.all([
          dashboardKPIService.getCards(),
          dashboardKPIService.getKPIs(),
        ]);

        if (cardsRes.success) setCards(cardsRes.cards || []);
        if (kpisRes.success) setKpis(kpisRes.kpis || {});

        // v2.96 - Fetch saved layout from config
        try {
          const configRes = await dashboardKPIService.getConfig();
          if (configRes.success && (configRes.config?.layout?.length ?? 0) > 0) {
            const savedLayout = configRes.config.layout!.map((item: any) => ({
              i: item.cardId,
              x: item.x,
              y: item.y,
              w: item.w,
              h: item.h,
            }));
            setLayouts({ lg: savedLayout, md: savedLayout, sm: savedLayout });
          }
        } catch {
          // No saved layout, use defaults
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // v2.96 - Generate default layout from card positions
  const defaultLayout = useMemo((): Layout[] => {
    return cards.map((card, index) => ({
      i: card.cardId,
      x: (index % 4) * 3,
      y: Math.floor(index / 4) * 2,
      w: 3,
      h: 2,
      minW: 2,
      minH: 2,
    }));
  }, [cards]);

  const currentLayout = layouts.lg || defaultLayout;

  // v2.96 - Handle layout change (debounced save)
  const handleLayoutChange = useCallback(
    (layout: Layout[]) => {
      if (!editMode) return;

      const mapped = layout.map((item) => ({
        cardId: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
      }));

      if (onLayoutChange) {
        onLayoutChange(mapped);
      }
    },
    [editMode, onLayoutChange]
  );

  // v2.96 - Trend icon
  const getTrendIcon = (direction: string | undefined) => {
    if (direction === 'up') return <TrendingUpIcon sx={{ color: 'success.main', fontSize: 18 }} />;
    if (direction === 'down') return <TrendingDownIcon sx={{ color: 'error.main', fontSize: 18 }} />;
    return <TrendingFlatIcon sx={{ color: 'text.secondary', fontSize: 18 }} />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2 }}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rounded" height={160} />
        ))}
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (cards.length === 0) {
    return <Alert severity="info">No KPI cards configured yet. Run the data pipeline to generate cards.</Alert>;
  }

  return (
    <ResponsiveGridLayout
      layouts={{ lg: currentLayout }}
      breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
      cols={{ lg: 12, md: 9, sm: 6, xs: 3 }}
      rowHeight={80}
      isDraggable={editMode}
      isResizable={editMode}
      onLayoutChange={handleLayoutChange}
      draggableHandle=".drag-handle"
      compactType="vertical"
      margin={[16, 16]}
    >
      {cards.map((card) => {
        const kpi = kpis[card.cardId?.replace('card-', '')] || kpis[card.cardId] || ({} as KPIValue);
        const sparklineData = kpi.sparkline || [];

        return (
          <div key={card.cardId}>
            <Card
              sx={{
                height: '100%',
                cursor: editMode ? 'default' : 'pointer',
                borderLeft: `4px solid ${card.color || theme.palette.primary.main}`,
                transition: 'box-shadow 0.2s',
                '&:hover': editMode ? {} : { boxShadow: 4 },
                position: 'relative',
              }}
              onClick={() => !editMode && onCardClick?.(card.cardId)}
            >
              {/* v2.96 - Edit mode controls */}
              {editMode && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    display: 'flex',
                    gap: 0.5,
                    zIndex: 1,
                  }}
                >
                  <IconButton size="small" className="drag-handle" aria-label="Drag to reorder card" sx={{ cursor: 'grab' }}>
                    <DragIcon fontSize="small" />
                  </IconButton>
                  {card.removable && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveCard?.(card.cardId);
                      }}
                      sx={{ color: 'error.main' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}

              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* Title */}
                <Typography variant="caption" color="text.secondary" noWrap>
                  {card.title}
                </Typography>

                {/* Value */}
                <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5 }}>
                  {formatValue(kpi.currentValue ?? null, card.format, card.formatOptions as any)}
                </Typography>

                {/* Trend */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  {getTrendIcon(kpi.changeDirection)}
                  <Typography
                    variant="caption"
                    sx={{
                      color: kpi.changeDirection === 'up' ? 'success.main' : kpi.changeDirection === 'down' ? 'error.main' : 'text.secondary',
                    }}
                  >
                    {kpi.changePercent != null ? `${Math.abs(kpi.changePercent).toFixed(1)}%` : '--'}
                  </Typography>
                </Box>

                {/* Sparkline */}
                {sparklineData.length > 1 && (
                  <Box sx={{ height: 30 }}>
                    <Sparklines data={sparklineData} height={30} margin={2}>
                      <SparklinesLine color={card.color || theme.palette.primary.main} style={{ strokeWidth: 1.5, fill: 'none' }} />
                    </Sparklines>
                  </Box>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })}
    </ResponsiveGridLayout>
  );
};

export default DashboardGrid;
